import type { FC } from 'react';
import { useParams } from 'react-router';
import { useMemoOne } from 'use-memo-one';
import type { DocumentsParams } from 'src/apis/document-exchange/types';
import { useOperatorDocumentsOutbox } from 'src/apis/document-exchange/use-operator-documents-outbox';
import { PageSpinner } from 'src/components/spinner-container';
import { isNetworkError } from 'src/hooks/use-axios';
import { useTitle } from 'src/hooks/use-title';
import { DocumentsFilterCard } from 'src/pages/shippers/documents/filter-card';
import { OperatorDocumentsTable } from 'src/pages/shippers/documents/table';
import { usePageParams } from 'src/pages/shippers/documents/use-page-params';

type Props = {
  operatorDocumentsOutboxLink: string;
};

export const ToShipperPage: FC<Props> = ({ operatorDocumentsOutboxLink }) => {
  useTitle('Document Overview');
  const pageParams = usePageParams();
  const { shipperUuid } = useParams<{ shipperUuid: string }>();

  const params = useMemoOne<DocumentsParams>(() => {
    const { start, pageSize, ...rest } = pageParams.value;
    return {
      offset: start,
      limit: pageSize,
      shipperId: shipperUuid,
      ...rest,
    };
  }, [pageParams.value]);

  const documents = useOperatorDocumentsOutbox({
    url: operatorDocumentsOutboxLink,
    params,
  });

  if (!documents.response && !isNetworkError(documents.error)) {
    return <PageSpinner />;
  }

  return (
    <div
      className={`
        grid items-center gap-2xl
        legacy:gap-[2.5rem]
      `}
    >
      <DocumentsFilterCard
        type="OUTBOX"
        pageParams={pageParams}
        documents={documents}
      />
      {documents.response && (
        <OperatorDocumentsTable
          type="OUTBOX"
          pageParams={pageParams}
          pending={documents.pending}
          documents={documents.response.data}
          showActions={false}
        />
      )}
    </div>
  );
};
